import { ExitToApp } from '@mui/icons-material';
import { Step, StepLabel, Stepper } from '@mui/material';
import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { RejectionBanner } from '@/components/admin-onboarding/RejectionBanner';
import { BeneficialOwnersStep } from '@/components/admin-onboarding/steps/BeneficialOwnersStep';
import { BusinessDetailsStep } from '@/components/admin-onboarding/steps/BusinessDetailsStep';
import { ContactInfoStep } from '@/components/admin-onboarding/steps/ContactInfoStep';
import { CorporateIdentifiersStep } from '@/components/admin-onboarding/steps/CorporateIdentifiersStep';
import { LegalDocumentsStep } from '@/components/admin-onboarding/steps/LegalDocumentsStep';
import { LegalIdentityStep } from '@/components/admin-onboarding/steps/LegalIdentityStep';
import { WaitingRoomStep } from '@/components/admin-onboarding/steps/WaitingRoomStep';
import { LoadingState, ErrorState } from '@/components/common';
import {
  useFunnelState,
  useSubmitStep,
  useGoToStep,
  useUploadDocuments,
  useDeleteDocument,
  useBeneficialOwners,
  useCreateBeneficialOwner,
  useDeleteBeneficialOwner,
  useUploadBeneficialOwnerDocs,
} from '@/hooks/queries/useAdminOnboarding';
import { useAuth } from '@/hooks/useAuth';
import type { CreateBeneficialOwnerDto, FunnelDocument } from '@/types/adminOnboarding';

const STEP_TITLES = [
  'Identidad Legal',
  'Contacto',
  'Negocio',
  'Identificadores',
  'Documentos',
  'Beneficiarios',
  'Resumen',
];

const toUploadedDoc = (doc: FunnelDocument) => ({
  id: doc.id,
  documentType: doc.documentType,
  originalFilename: doc.originalFilename,
  fileSize: doc.fileSize,
});

export const AdminFunnelPage: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [uploadingDocType, setUploadingDocType] = useState<string | null>(null);

  const { data: funnelState, isLoading, isError, refetch } = useFunnelState();
  const { data: ubos = [] } = useBeneficialOwners();

  const submitStep = useSubmitStep();
  const goToStep = useGoToStep();
  const uploadDocs = useUploadDocuments();
  const deleteDoc = useDeleteDocument();
  const createUbo = useCreateBeneficialOwner();
  const deleteUbo = useDeleteBeneficialOwner();
  const uploadUboDocs = useUploadBeneficialOwnerDocs();

  if (isLoading) return <LoadingState message="Cargando formulario..." />;
  if (isError || !funnelState) {
    return (
      <ErrorState title="Error al cargar el formulario" showRetry onRetry={() => void refetch()} />
    );
  }

  const currentOrder = funnelState.currentStep.order;
  const stepIndex = currentOrder - 1;
  const stepData = funnelState.userFunnel.data;
  const currentStepDocs =
    (funnelState as unknown as { documents?: FunnelDocument[] }).documents ?? [];
  const docsForCurrentStep = currentStepDocs
    .filter(d => d.stepOrder === currentOrder)
    .map(toUploadedDoc);

  const isRejected = funnelState.userFunnel.reviewStatus === 'rejected';

  const handleUploadDoc = async (documentType: string, file: File) => {
    const entityType = currentOrder === 1 ? 'legal_rep' : 'company';
    const formData = new FormData();
    formData.append('files', file);
    formData.append('documentType', documentType);
    formData.append('entityType', entityType);
    setUploadingDocType(documentType);
    try {
      await uploadDocs.mutateAsync({ stepOrder: currentOrder, formData });
    } catch (_) {
      toast.error('Error al subir el documento.');
    } finally {
      setUploadingDocType(null);
    }
  };

  const handleDeleteDoc = async (documentId: string) => {
    try {
      await deleteDoc.mutateAsync(documentId);
    } catch {
      toast.error('Error al eliminar el documento.');
    }
  };

  const handleSubmitStep = async (data: object) => {
    try {
      await submitStep.mutateAsync(data as Record<string, unknown>);
      toast.success('Paso guardado exitosamente.');
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const handleAddUbo = async (data: CreateBeneficialOwnerDto) => {
    try {
      await createUbo.mutateAsync(data);
    } catch {
      toast.error('Error al agregar beneficiario.');
    }
  };

  const handleDeleteUbo = async (id: string) => {
    try {
      await deleteUbo.mutateAsync(id);
    } catch {
      toast.error('Error al eliminar beneficiario.');
    }
  };

  const handleUploadUboDoc = async (uboId: string, documentType: string, file: File) => {
    const ubo =
      ubos.find(u => u.id === uboId) ??
      ubos.flatMap(u => u.children ?? []).find(u => u.id === uboId);
    const entityType = ubo?.type === 'company' ? 'ubo_company' : 'ubo_individual';
    const formData = new FormData();
    formData.append('files', file);
    formData.append('documentType', documentType);
    formData.append('entityType', entityType);
    formData.append('entityId', uboId);
    try {
      await uploadUboDocs.mutateAsync({ uboId, formData });
    } catch {
      toast.error('Error al subir el documento del beneficiario.');
    }
  };

  const handleStepBack = async () => {
    const prevOrder = currentOrder - 1;
    if (prevOrder < 1) return;
    try {
      await goToStep.mutateAsync(prevOrder);
    } catch {
      toast.error('Error al regresar al paso anterior.');
    }
  };

  // When funnel is finished or in waiting room (step 7)
  if (funnelState.isFinished || currentOrder === 7) {
    const reviewStatus = funnelState.userFunnel.reviewStatus;
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 py-10">
          {/* Header row */}
          <div className="flex justify-end mb-2">
            <button
              onClick={() => void logout()}
              className="flex items-center gap-1.5 text-sm mb-4 text-blue-600 hover:text-red-500 transition-colors"
            >
              <ExitToApp fontSize="small" />
              Cerrar sesión
            </button>
          </div>

          <div className="mb-8">
            <Stepper activeStep={6} alternativeLabel>
              {STEP_TITLES.map(label => (
                <Step key={label} completed>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </div>

          {isRejected && funnelState.userFunnel.rejectionMessage && (
            <RejectionBanner message={funnelState.userFunnel.rejectionMessage} />
          )}

          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <WaitingRoomStep
              reviewStatus={reviewStatus}
              rejectionMessage={funnelState.userFunnel.rejectionMessage}
              stepData={stepData}
              onConfirmSubmit={() => void handleSubmitStep({})}
              onBack={() => void handleStepBack()}
              isSubmitting={submitStep.isPending}
            />
          </div>

          {reviewStatus === 'approved' && (
            <div className="mt-4 text-center">
              <button
                onClick={() => void navigate('/dashboard-empresarial', { replace: true })}
                className="px-6 py-2.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors"
              >
                Ir a mi panel
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  const renderStep = () => {
    const initialData = stepData[currentOrder] as Record<string, unknown> | undefined;

    switch (currentOrder) {
      case 1:
        return (
          <LegalIdentityStep
            initialData={initialData as Parameters<typeof LegalIdentityStep>[0]['initialData']}
            uploadedDocs={docsForCurrentStep}
            onSubmit={(data: object) => void handleSubmitStep(data)}
            onUploadDoc={(type: string, file: File) => void handleUploadDoc(type, file)}
            onDeleteDoc={(id: string) => void handleDeleteDoc(id)}
            isSubmitting={submitStep.isPending}
            uploadingDocType={uploadingDocType}
          />
        );
      case 2:
        return (
          <ContactInfoStep
            initialData={initialData as Parameters<typeof ContactInfoStep>[0]['initialData']}
            onSubmit={(data: object) => void handleSubmitStep(data)}
            onBack={() => void handleStepBack()}
            isSubmitting={submitStep.isPending}
          />
        );
      case 3:
        return (
          <BusinessDetailsStep
            initialData={initialData as Parameters<typeof BusinessDetailsStep>[0]['initialData']}
            onSubmit={(data: object) => void handleSubmitStep(data)}
            onBack={() => void handleStepBack()}
            isSubmitting={submitStep.isPending}
          />
        );
      case 4:
        return (
          <CorporateIdentifiersStep
            initialData={
              initialData as Parameters<typeof CorporateIdentifiersStep>[0]['initialData']
            }
            onSubmit={(data: object) => void handleSubmitStep(data)}
            onBack={() => void handleStepBack()}
            isSubmitting={submitStep.isPending}
          />
        );
      case 5:
        return (
          <LegalDocumentsStep
            uploadedDocs={docsForCurrentStep}
            onUpload={(type: string, file: File) => void handleUploadDoc(type, file)}
            onDelete={(id: string) => void handleDeleteDoc(id)}
            onSubmit={() => void handleSubmitStep({})}
            onBack={() => void handleStepBack()}
            isSubmitting={submitStep.isPending}
            uploadingDocType={uploadingDocType}
          />
        );
      case 6:
        return (
          <BeneficialOwnersStep
            ubos={ubos}
            onAddUbo={(data: CreateBeneficialOwnerDto) => void handleAddUbo(data)}
            onDeleteUbo={(id: string) => void handleDeleteUbo(id)}
            onUploadDoc={(uboId: string, type: string, file: File) =>
              handleUploadUboDoc(uboId, type, file)
            }
            onDeleteDoc={(id: string) => void handleDeleteDoc(id)}
            onSubmit={() => void handleSubmitStep({})}
            onBack={() => void handleStepBack()}
            isSubmitting={submitStep.isPending}
            isAdding={createUbo.isPending}
          />
        );
      default:
        return <p className="text-gray-500 text-sm">Paso no reconocido.</p>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-3xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-end mb-2">
            <button
              onClick={() => void logout()}
              className="flex items-center gap-1.5 text-sm mb-4 text-blue-600 hover:text-red-500 transition-colors"
            >
              <ExitToApp fontSize="small" />
              Cerrar sesión
            </button>
          </div>
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900">Registro de Administrador</h1>
            <p className="text-sm text-gray-500 mt-1">Completa los pasos para activar tu cuenta.</p>
          </div>
        </div>

        {/* Stepper */}
        <div className="mb-8">
          <Stepper activeStep={stepIndex} alternativeLabel>
            {STEP_TITLES.map((label, idx) => (
              <Step key={label} completed={idx < stepIndex}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </div>

        {/* Rejection Banner */}
        {isRejected && funnelState.userFunnel.rejectionMessage && (
          <RejectionBanner
            message={funnelState.userFunnel.rejectionMessage}
            stepTitle={STEP_TITLES[stepIndex]}
          />
        )}

        {/* Step Content */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-6">
            Paso {currentOrder}: {funnelState.currentStep.title}
          </h2>
          {renderStep()}
        </div>
      </div>
    </div>
  );
};
